import React, { useRef, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { Layer, Shape } from 'react-konva';
import * as d3 from 'd3';
import 'd3-contour';

const ContourMapLayer = ({
  gridValues,
  gridRect,
  scale,
  contourValues,
  autoContourValues,
  contourColors,
  gridScaleX,
  gridScaleY,
  dxfScale,
  heatmap,
  greyscale,
  flipY,
  worldHeight,
}) => {
  const shapeRef = useRef();
  const throttleRef = useRef(false); // throttle flag
  const worldHeightValue = useMemo(() => {
    if (Number.isFinite(worldHeight) && worldHeight > 0) return worldHeight;
    return Number(gridRect?.origin?.y?.length) || 0;
  }, [worldHeight, gridRect]);
  const shouldFlipY = Boolean(flipY && worldHeightValue > 0);
  const startY = Number(gridRect?.origin?.y?.start) || 0;

  // Compute grid dimensions based on gridRect.
  const numCols = useMemo(() => {
    if (!gridRect || !gridRect.origin.x.length || !gridRect.xSpacing) return 0;
    return Math.round(gridRect.origin.x.length / gridRect.xSpacing);
  }, [gridRect]);

  const numRows = useMemo(() => {
    if (!gridRect || !gridRect.origin.y.length || !gridRect.ySpacing) return 0;
    return Math.round(gridRect.origin.y.length / gridRect.ySpacing);
  }, [gridRect]);

  // Derived values from gridValues.
  const dataMax = useMemo(() => {
    if (!gridValues?.length) return 0;
    return Math.max(...gridValues);
  }, [gridValues]);
  const dataMin = 1;

  // Create 7 evenly spaced thresholds from dataMin to dataMax.
  const thresholds = useMemo(() => {
    if (!autoContourValues) {
      return contourValues;
    } else {
      return d3.range(7).map(i => dataMin + (i / 7) * (dataMax - dataMin));
    }
  }, [autoContourValues, contourValues, dataMin, dataMax]);

  // Create a color scale using d3.interpolateSpectral.
  const colorScale = useMemo(
    () => d3.scaleSequential(d3.interpolateSpectral).domain([dataMax, dataMin]),
    [dataMax, dataMin]
  );

  // Greyscale color scale: larger value = lighter, smaller = darker
  const greyscaleScale = useMemo(
    () => d3.scaleLinear().domain([dataMin, dataMax]).range(["#222", "#fff"]),
    [dataMin, dataMax]
  );

  // Generate contours using d3-contour.
  const contours = useMemo(() => {
    if (
      !gridValues?.length ||
      numCols <= 0 ||
      numRows <= 0 ||
      thresholds?.length === 0
    )
      return [];

    const contourGenerator = d3
      .contours()
      .size([numCols, numRows])
      .thresholds(thresholds);

    return contourGenerator(gridValues);
  }, [gridValues, numCols, numRows, thresholds]);

  const drawContours = (ctx) => {
    contours.forEach((contour, i) => {
      const projection = d3.geoTransform({
        point: function (x, y) {
          // x, y are grid cell indices
          const realX = gridRect.origin.x.start + x * gridRect.xSpacing;
          const realY = startY + (y * gridRect.ySpacing);
          const px = realX * gridScaleX * dxfScale;
          const worldY = shouldFlipY ? (worldHeightValue - realY) : realY;
          const py = worldY * gridScaleY * dxfScale;
          this.stream.point(px, py);
        },
      });
      const pathGenerator = d3.geoPath().projection(projection);
      const pathData = pathGenerator(contour);
      const path2d = new Path2D(pathData);

      if (heatmap) {
        // Fill contour
        let fillColor;
        fillColor = greyscale ? greyscaleScale(contour.value) : colorScale(contour.value);
        ctx.fillStyle = fillColor;
        ctx.globalAlpha = 0.3;
        ctx.fill(path2d);
        ctx.globalAlpha = 0.3;
      } else {
        // Stroke only
        ctx.strokeStyle = autoContourValues
          ? colorScale(contour.value)
          : contourColors[i];
        // Keep contours in the same visual family as grid/mask lines, but lighter.
        const safeScale = Math.max(0.0001, Number(scale) || 1);
        ctx.lineWidth = Math.max(0.05, 0.16 / safeScale);
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke(path2d);
      }
    });
  };

  // The sceneFunc for the Shape uses throttleRef to prevent continuous redraws.
  const sceneFunc = (ctx, shape) => {
    drawContours(ctx);
  };

  // Reset the throttle flag unconditionally whenever gridValues changes.
  useEffect(() => {
    throttleRef.current = false;
    if (shapeRef.current && shapeRef.current.getLayer()) {
      shapeRef.current.getLayer().batchDraw();
    }
  }, [gridValues]);

  return (
    <Layer>
      <Shape ref={shapeRef} sceneFunc={sceneFunc} />
    </Layer>
  );
};

export default ContourMapLayer;
